import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  History, User, FileText, Calendar, Users, CreditCard, Megaphone, 
  ChevronLeft, ChevronRight, Filter, RefreshCw, Building2, CheckCircle,
  Clock, XCircle, ChevronDown, ChevronUp
} from 'lucide-react';
import Layout from '@/components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ITEMS_PER_PAGE = 20;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const actionLabels = {
  create: { label: 'Criação', color: 'bg-green-500' },
  update: { label: 'Atualização', color: 'bg-blue-500' },
  delete: { label: 'Exclusão', color: 'bg-red-500' },
  login: { label: 'Login', color: 'bg-purple-500' },
  logout: { label: 'Logout', color: 'bg-slate-500' }
};

const entityLabels = {
  client: { label: 'Cliente', icon: Users },
  partner: { label: 'Parceiro', icon: User },
  period: { label: 'Período', icon: Calendar },
  announcement: { label: 'Comunicado', icon: Megaphone },
  payment: { label: 'Pagamento', icon: CreditCard },
  auth: { label: 'Autenticação', icon: User },
  service: { label: 'Serviço', icon: FileText },
  document: { label: 'Documento', icon: FileText }
};

const paymentStatusLabels = {
  pago: { label: 'Pago', color: 'bg-green-500', icon: CheckCircle },
  processando: { label: 'Processando', color: 'bg-yellow-500', icon: Clock },
  nao_pago: { label: 'Não Pago', color: 'bg-red-500', icon: XCircle }
};

const AuditPage = ({ user, onLogout }) => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [clientsSummary, setClientsSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingClients, setLoadingClients] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedPartners, setExpandedPartners] = useState({});
  const [activeTab, setActiveTab] = useState('logs');
  const [filters, setFilters] = useState({
    entity_type: '',
    action: ''
  });

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [currentPage, filters]);

  useEffect(() => {
    if (activeTab === 'clients' && !clientsSummary) {
      fetchClientsSummary();
    }
  }, [activeTab]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: ITEMS_PER_PAGE,
        skip: (currentPage - 1) * ITEMS_PER_PAGE
      });
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.action) params.append('action', filters.action);

      const response = await axios.get(`${API}/audit-logs?${params}`, getAuthHeaders());
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Erro ao carregar logs de auditoria');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/audit-logs/stats`, getAuthHeaders());
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas');
    }
  };

  const fetchClientsSummary = async () => {
    setLoadingClients(true);
    try {
      const response = await axios.get(`${API}/audit-logs/clients-summary`, getAuthHeaders());
      setClientsSummary(response.data);
    } catch (error) {
      toast.error('Erro ao carregar resumo de clientes');
    } finally {
      setLoadingClients(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const togglePartnerExpand = (partnerId) => {
    setExpandedPartners(prev => ({
      ...prev,
      [partnerId]: !prev[partnerId]
    }));
  };

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const clearFilters = () => {
    setFilters({ entity_type: '', action: '' });
    setCurrentPage(1);
  };

  const refreshAll = () => {
    fetchLogs();
    fetchStats();
    if (activeTab === 'clients') {
      fetchClientsSummary();
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <History className="h-8 w-8 text-primary" />
              Auditoria e Relatórios
            </h1>
            <p className="text-muted-foreground mt-1">
              Acompanhe todas as atividades do sistema
            </p>
          </div>
          <Button variant="outline" onClick={refreshAll}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-green-600">{stats.by_entity.client || 0}</p>
                <p className="text-sm text-muted-foreground">Clientes</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-blue-600">{stats.by_entity.payment || 0}</p>
                <p className="text-sm text-muted-foreground">Pagamentos</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-green-600">{stats.by_action.create || 0}</p>
                <p className="text-sm text-muted-foreground">Criações</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-amber-600">{stats.by_action.update || 0}</p>
                <p className="text-sm text-muted-foreground">Atualizações</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-purple-600">{stats.by_action.login || 0}</p>
                <p className="text-sm text-muted-foreground">Logins</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-2xl font-bold">{stats.total || 0}</p>
                <p className="text-sm text-muted-foreground">Total</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
            <TabsTrigger value="logs" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Histórico
            </TabsTrigger>
            <TabsTrigger value="clients" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Clientes por Parceiro
            </TabsTrigger>
          </TabsList>

          {/* Tab: Logs */}
          <TabsContent value="logs" className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Filtros:</span>
                  </div>
                  
                  <Select 
                    value={filters.entity_type || 'all'} 
                    onValueChange={(v) => { setFilters(f => ({ ...f, entity_type: v === 'all' ? '' : v })); setCurrentPage(1); }}
                  >
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os tipos</SelectItem>
                      <SelectItem value="client">Cliente</SelectItem>
                      <SelectItem value="payment">Pagamento</SelectItem>
                      <SelectItem value="partner">Parceiro</SelectItem>
                      <SelectItem value="period">Período</SelectItem>
                      <SelectItem value="announcement">Comunicado</SelectItem>
                      <SelectItem value="service">Serviço</SelectItem>
                      <SelectItem value="auth">Autenticação</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select 
                    value={filters.action || 'all'} 
                    onValueChange={(v) => { setFilters(f => ({ ...f, action: v === 'all' ? '' : v })); setCurrentPage(1); }}
                  >
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="Ação" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas as ações</SelectItem>
                      <SelectItem value="create">Criação</SelectItem>
                      <SelectItem value="update">Atualização</SelectItem>
                      <SelectItem value="delete">Exclusão</SelectItem>
                      <SelectItem value="login">Login</SelectItem>
                    </SelectContent>
                  </Select>

                  {(filters.entity_type || filters.action) && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      Limpar filtros
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Logs List */}
            <Card>
              <CardHeader>
                <CardTitle>Registros de Atividade</CardTitle>
                <CardDescription>{total} registro(s) encontrado(s)</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">Carregando...</div>
                ) : logs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Nenhum registro encontrado
                  </div>
                ) : (
                  <ScrollArea className="h-[500px]">
                    <div className="space-y-3">
                      {logs.map((log) => {
                        const actionInfo = actionLabels[log.action] || { label: log.action, color: 'bg-slate-500' };
                        const entityInfo = entityLabels[log.entity_type] || { label: log.entity_type, icon: FileText };
                        const EntityIcon = entityInfo.icon;

                        return (
                          <div
                            key={log.id}
                            className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                          >
                            <div className={`w-2 h-2 rounded-full mt-2 ${actionInfo.color}`} />
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <Badge variant="outline" className="flex items-center gap-1">
                                  <EntityIcon className="h-3 w-3" />
                                  {entityInfo.label}
                                </Badge>
                                <Badge className={`${actionInfo.color} text-white`}>
                                  {actionInfo.label}
                                </Badge>
                                {log.entity_name && (
                                  <span className="text-sm font-medium truncate">
                                    {log.entity_name}
                                  </span>
                                )}
                              </div>
                              
                              <p className="text-sm text-muted-foreground">
                                Por <span className="font-medium">{log.user_email}</span>
                                {log.user_role && (
                                  <span className="text-xs ml-1">({log.user_role})</span>
                                )}
                              </p>
                              
                              {log.details && Object.keys(log.details).length > 0 && (
                                <div className="mt-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded space-y-1">
                                  {log.details.action && <p className="font-medium">{log.details.action}</p>}
                                  {log.details.parceiro_nome && (
                                    <p>Parceiro: {log.details.parceiro_nome}</p>
                                  )}
                                  {log.details.tipo_pessoa && (
                                    <p>Tipo: {log.details.tipo_pessoa === 'fisica' ? 'Pessoa Física' : 'Pessoa Jurídica'}</p>
                                  )}
                                  {log.details.cpf_cnpj && (
                                    <p>CPF/CNPJ: {log.details.cpf_cnpj}</p>
                                  )}
                                  {log.details.old_status && log.details.new_status && (
                                    <p>Status: {log.details.old_status} → {log.details.new_status}</p>
                                  )}
                                  {log.details.status_anterior && log.details.novo_status && (
                                    <p>Pagamento: {log.details.status_anterior} → {log.details.novo_status}</p>
                                  )}
                                  {log.details.quantidade_clientes && (
                                    <p>{log.details.quantidade_clientes} cliente(s)</p>
                                  )}
                                  {log.details.clientes && log.details.clientes.length > 0 && (
                                    <p>Clientes: {log.details.clientes.join(', ')}</p>
                                  )}
                                  {log.details.periodo && (
                                    <p>Período: {log.details.periodo}</p>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            <div className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDate(log.created_at)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(p => p - 1)}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Página {currentPage} de {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(p => p + 1)}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Clients by Partner */}
          <TabsContent value="clients" className="space-y-4">
            {/* Summary Cards */}
            {clientsSummary && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold">{clientsSummary.totais.total_parceiros}</p>
                    <p className="text-sm text-muted-foreground">Parceiros</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold">{clientsSummary.totais.total_clientes}</p>
                    <p className="text-sm text-muted-foreground">Clientes</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold text-green-600">{clientsSummary.totais.total_pagos}</p>
                    <p className="text-sm text-muted-foreground">Pagos</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold text-yellow-600">{clientsSummary.totais.total_processando}</p>
                    <p className="text-sm text-muted-foreground">Processando</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold text-red-600">{clientsSummary.totais.total_nao_pagos}</p>
                    <p className="text-sm text-muted-foreground">Não Pagos</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Partners List */}
            <Card>
              <CardHeader>
                <CardTitle>Clientes por Parceiro</CardTitle>
                <CardDescription>Visualize todos os clientes agrupados por parceiro</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingClients ? (
                  <div className="text-center py-8 text-muted-foreground">Carregando...</div>
                ) : !clientsSummary || clientsSummary.parceiros.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Nenhum parceiro encontrado
                  </div>
                ) : (
                  <div className="space-y-4">
                    {clientsSummary.parceiros.map((partner) => (
                      <Card key={partner.parceiro_id} className="overflow-hidden">
                        <div 
                          className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => togglePartnerExpand(partner.parceiro_id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-primary/10 rounded-full">
                                <Building2 className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <p className="font-semibold">{partner.parceiro_nome}</p>
                                <p className="text-sm text-muted-foreground">{partner.parceiro_email}</p>
                                {partner.parceiro_empresa && (
                                  <p className="text-xs text-muted-foreground">{partner.parceiro_empresa}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary">{partner.total_clientes} cliente(s)</Badge>
                                <Badge className="bg-green-500 text-white">{partner.pagos} pago(s)</Badge>
                                <Badge className="bg-yellow-500 text-white">{partner.processando} proc.</Badge>
                                <Badge className="bg-red-500 text-white">{partner.nao_pagos} não pago(s)</Badge>
                              </div>
                              {expandedPartners[partner.parceiro_id] ? (
                                <ChevronUp className="w-5 h-5 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-muted-foreground" />
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {expandedPartners[partner.parceiro_id] && (
                          <div className="border-t bg-muted/30">
                            <div className="p-4">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-left text-muted-foreground border-b">
                                    <th className="pb-2 font-medium">Nome</th>
                                    <th className="pb-2 font-medium">Tipo</th>
                                    <th className="pb-2 font-medium">CPF/CNPJ</th>
                                    <th className="pb-2 font-medium">Telefone</th>
                                    <th className="pb-2 font-medium">Pagamento</th>
                                    <th className="pb-2 font-medium">Cadastro</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {partner.clientes.map((client) => {
                                    const statusInfo = paymentStatusLabels[client.status_pagamento] || paymentStatusLabels.nao_pago;
                                    const StatusIcon = statusInfo.icon;
                                    return (
                                      <tr key={client.id} className="border-b last:border-0">
                                        <td className="py-2 font-medium">{client.nome}</td>
                                        <td className="py-2 capitalize">{client.tipo === 'fisica' ? 'PF' : 'PJ'}</td>
                                        <td className="py-2">{client.cpf_cnpj}</td>
                                        <td className="py-2">{client.telefone || '-'}</td>
                                        <td className="py-2">
                                          <Badge className={`${statusInfo.color} text-white flex items-center gap-1 w-fit`}>
                                            <StatusIcon className="w-3 h-3" />
                                            {statusInfo.label}
                                          </Badge>
                                        </td>
                                        <td className="py-2 text-muted-foreground text-xs">
                                          {formatDate(client.data_cadastro)}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default AuditPage;
